// /src/components/admin/DesignSystemTab.jsx
// Design System Preview — admin tab showing all StandardButton variants,
// design tokens, and contextual page mockups with responsive viewport slider.

import React, { useState, useRef, useCallback } from 'react';
import StandardButton from '../core/StandardButton';
import { tokens, variantMap } from '../../styles/designTokens';

// ---------------------------------------------------------------------------
// Variant registry for the gallery
// ---------------------------------------------------------------------------
const VARIANTS = [
  {
    id: 'context',
    label: 'Context',
    desc: 'Back button, navigation. Colour switches with locality/groups context.',
    color: tokens.colors.contextLocality,
    colorAlt: tokens.colors.contextGroups,
    shape: 'pill',
    iconName: 'ArrowLeft',
    buttonLabel: 'Back',
    usedIn: 'HeaderButtonsDetail, HeaderButtonsForm, HeaderEventDetail, HeaderArticleDetail',
  },
  {
    id: 'action',
    label: 'Action',
    desc: 'Positive, creative, forward-moving. Create, Publish, Save, Post.',
    color: tokens.colors.action,
    shape: 'pill',
    iconName: 'Plus',
    buttonLabel: 'Create',
    usedIn: 'HeaderButtonsFeed, FooterButtonsDetail (Save)',
  },
  {
    id: 'destructive',
    label: 'Destructive',
    desc: 'Removal, cancellation, irreversible. Always confirms before executing.',
    color: tokens.colors.destructive,
    shape: 'pill',
    iconName: 'X',
    buttonLabel: 'Cancel',
    usedIn: 'FooterButtonsDetail (Cancel)',
  },
  {
    id: 'utility',
    label: 'Utility',
    desc: 'Non-committal, exploratory. Preview, Save Draft.',
    color: tokens.colors.utility,
    shape: 'pill',
    iconName: 'Eye',
    buttonLabel: 'Preview',
    usedIn: 'FooterButtonsForm (Preview, Save Draft)',
  },
  {
    id: 'identity',
    label: 'Identity',
    desc: 'User presence and support. Profile, Help, Log In circles.',
    color: tokens.colors.identity,
    shape: 'circle',
    iconName: 'User',
    buttonLabel: null,
    usedIn: 'HeaderButtonsFeed (Profile), FooterContainer (Help)',
  },
  {
    id: 'ghost',
    label: 'Ghost',
    desc: 'Subtle container. Dilute teal background with border. Share button.',
    color: tokens.colors.actionDilute,
    shape: 'ghost',
    iconName: 'Share2',
    buttonLabel: 'Share',
    usedIn: 'ArticleDetail sidebar (Share)',
  },
  {
    id: 'inline',
    label: 'Inline',
    desc: 'No container at all. Like, Dislike, Reply, Flag. Feels like content.',
    color: 'transparent',
    shape: 'inline',
    iconName: 'ThumbsUp',
    buttonLabel: '24',
    usedIn: 'NewsCard, ChatDetail, ArticleDetail (all inline interactions)',
  },
  {
    id: 'admin',
    label: 'Admin',
    desc: 'Platform administration only. Visible to users.is_sys_admin accounts only.',
    color: tokens.colors.admin,
    shape: 'circle',
    iconName: 'Shield',
    buttonLabel: null,
    usedIn: 'HeaderButtonsFeed (Admin shield)',
  },
];

// Variant colour dots for the locator bars
const VARIANT_DOT_COLORS = {
  context:     tokens.colors.contextLocality,
  action:      tokens.colors.action,
  destructive: tokens.colors.destructive,
  utility:     tokens.colors.utility,
  identity:    tokens.colors.identity,
  ghost:       tokens.colors.actionDiluteText,
  inline:      tokens.colors.textSecondary,
  admin:       tokens.colors.admin,
};

// ---------------------------------------------------------------------------
// Colour swatches
// ---------------------------------------------------------------------------
const COLOR_SWATCHES = [
  { name: 'Locality Blue',    hex: tokens.colors.contextLocality,   use: 'Context variant (locality)' },
  { name: 'Groups Mauve',     hex: tokens.colors.contextGroups,     use: 'Context variant (groups)' },
  { name: 'Action Teal',      hex: tokens.colors.action,            use: 'Action variant' },
  { name: 'Identity Orange',  hex: tokens.colors.identity,          use: 'Identity & Star rating' },
  { name: 'Destructive Red',  hex: tokens.colors.destructive,       use: 'Destructive variant' },
  { name: 'Utility Purple',   hex: tokens.colors.utility,           use: 'Utility variant' },
  { name: 'Admin Green',      hex: tokens.colors.admin,             use: 'Admin variant' },
  { name: 'Header/Footer BG', hex: tokens.colors.headerBackground,  use: 'Header & Footer zones' },
  { name: 'Content Bar',      hex: tokens.colors.contentBar,        use: 'Header white content bar' },
];

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const SectionTitle = ({ children }) => (
  <h2 style={{
    fontSize: '18px',
    fontWeight: '700',
    color: '#1A1A1A',
    margin: '0 0 16px 0',
    paddingBottom: '8px',
    borderBottom: '2px solid #E5E7EB',
    fontFamily: "'OpenSauceSans', sans-serif",
  }}>
    {children}
  </h2>
);

const VariantDot = ({ variant, size = 10 }) => (
  <span style={{
    display: 'inline-block',
    width: size,
    height: size,
    borderRadius: '50%',
    backgroundColor: VARIANT_DOT_COLORS[variant] ?? '#999',
    border: variant === 'inline' ? '1px solid #999' : 'none',
    flexShrink: 0,
  }} />
);

// Static pill lookalike — used to show context variant in groups colour without
// needing a ThemeProvider override. StandardButton variant="context" always reads
// the live theme (locality blue in admin), so we render a styled div instead.
const FakePill = ({ color, iconChar = '←', label }) => {
  const sz = tokens.button.pill.desktop;
  return (
    <div style={{
      display:        'inline-flex',
      alignItems:     'center',
      gap:            `${sz.gap}px`,
      height:         `${sz.height}px`,
      paddingLeft:    `${sz.paddingH}px`,
      paddingRight:   `${sz.paddingH}px`,
      borderRadius:   `${sz.borderRadius}px`,
      background:     color,
      color:          '#fff',
      fontSize:       `${sz.fontSize}px`,
      fontWeight:     sz.fontWeight,
      fontFamily:     tokens.typography.fontFamily,
      letterSpacing:  tokens.typography.buttonLetterSpacing,
      whiteSpace:     'nowrap',
      userSelect:     'none',
      flexShrink:     0,
    }}>
      <span style={{ fontSize: `${sz.iconSize}px`, lineHeight: 1 }}>{iconChar}</span>
      {label && <span>{label}</span>}
    </div>
  );
};

// Button locator entry — clickable, scrolls to highlighted element
const LocatorEntry = ({ label, variant, targetId, onLocate }) => (
  <button
    onClick={() => onLocate(targetId)}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '5px',
      background: 'rgba(0,0,0,0.05)',
      border: '1px solid rgba(0,0,0,0.1)',
      borderRadius: '12px',
      padding: '3px 10px',
      fontSize: '12px',
      cursor: 'pointer',
      color: '#333',
      fontFamily: "'OpenSauceSans', sans-serif",
      transition: 'background 0.15s',
    }}
    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.1)'; }}
    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.05)'; }}
  >
    <VariantDot variant={variant} size={8} />
    {label}
  </button>
);

// Button locator bar — shown above each mockup
const LocatorBar = ({ mockupTitle, zones, onLocate }) => (
  <div style={{
    backgroundColor: '#F8F9FA',
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '12px',
  }}>
    <div style={{ fontWeight: '700', fontSize: '13px', color: '#374151', marginBottom: '10px', fontFamily: "'OpenSauceSans', sans-serif" }}>
      {mockupTitle} — Button Map
    </div>
    {zones.map(zone => (
      <div key={zone.zone} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '6px' }}>
        <span style={{ fontSize: '11px', color: '#6B7280', minWidth: '60px', paddingTop: '4px', fontFamily: "'OpenSauceSans', sans-serif" }}>
          {zone.zone}:
        </span>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {zone.buttons.map(btn => (
            <LocatorEntry key={btn.id} label={btn.label} variant={btn.variant} targetId={btn.id} onLocate={onLocate} />
          ))}
        </div>
      </div>
    ))}
    <div style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '12px', fontSize: '11px', color: '#9CA3AF', fontFamily: "'OpenSauceSans', sans-serif" }}>
      {Object.entries(VARIANT_DOT_COLORS).map(([v, c]) => (
        <span key={v} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <VariantDot variant={v} size={8} /> {v}
        </span>
      ))}
    </div>
  </div>
);

// Mockup placeholder card
const PlaceholderCard = ({ title, snippet, id }) => (
  <div id={id} style={{
    background: 'white',
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    padding: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  }}>
    <div style={{ fontWeight: '600', fontSize: '13px', color: '#1A1A1A', fontFamily: "'OpenSauceSans', sans-serif" }}>{title}</div>
    <div style={{ fontSize: '12px', color: '#6B7280', lineHeight: '1.5', fontFamily: "'OpenSauceSans', sans-serif" }}>{snippet}</div>
    <div style={{ display: 'flex', gap: '12px', paddingTop: '4px' }}>
      <StandardButton variant="inline" iconName="ThumbsUp" label="14" />
      <StandardButton variant="inline" iconName="ThumbsDown" label="2" />
      <StandardButton variant="inline" iconName="MessageSquare" label="7" />
      <StandardButton variant="inline" iconName="Star" label="Rate" />
    </div>
  </div>
);

// Mock header bar for mockups
const MockHeader = ({ title, context = 'locality', showBack = false, showProfile = true, showCreate = false }) => {
  const contextColor = context === 'groups' ? tokens.colors.contextGroups : tokens.colors.contextLocality;
  return (
    <div style={{
      backgroundColor: '#EDF2F8',
      borderBottom: '1px solid #DDE3ED',
      padding: '8px 16px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      borderRadius: '8px 8px 0 0',
      minHeight: '52px',
    }}>
      {/* Logo placeholder */}
      <div style={{ width: '32px', height: '32px', borderRadius: '6px', backgroundColor: contextColor, flexShrink: 0 }} />
      {showBack && <StandardButton variant="context" iconName="ArrowLeft" label="Back" />}
      <div style={{ flex: 1, textAlign: 'center', fontWeight: '700', fontSize: '14px', color: contextColor, fontFamily: "'OpenSauceSans', sans-serif" }}>
        {title}
      </div>
      {showCreate && <StandardButton variant="action" iconName="Plus" label="Create" />}
      {showProfile && <StandardButton variant="identity" iconName="User" />}
    </div>
  );
};

// Mock footer bar for mockups
const MockFooter = ({ type = 'feed' }) => (
  <div style={{
    backgroundColor: '#EDF2F8',
    borderTop: '1px solid #DDE3ED',
    padding: '10px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderRadius: '0 0 8px 8px',
    minHeight: '52px',
  }}>
    <div style={{ fontSize: '11px', color: '#6B7280', fontFamily: "'OpenSauceSans', sans-serif" }}>OurNet</div>
    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
      {type === 'form' && (
        <>
          <StandardButton variant="destructive" iconName="X" label="Cancel" confirmBefore={false} />
          <StandardButton variant="utility" iconName="Eye" label="Preview" />
          <StandardButton variant="action" iconName="Save" label="Publish" />
        </>
      )}
      {type === 'feed' && (
        <>
          <StandardButton variant="action" iconName="ArrowUp" label="Top" showWhen="always" />
          <StandardButton variant="action" iconName="Plus" label="More" showWhen="always" />
        </>
      )}
      <StandardButton variant="identity" iconName="HelpCircle" />
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Main tab component
// ---------------------------------------------------------------------------
const DesignSystemTab = () => {
  const [previewWidth, setPreviewWidth] = useState(1200);
  const previewRef = useRef(null);

  // Viewport preset shortcuts
  const PRESETS = [
    { label: 'Phone (375px)',    width: 375 },
    { label: 'Tablet (768px)',   width: 768 },
    { label: 'Desktop (1200px)', width: 1200 },
    { label: 'Wide (1400px)',    width: 1400 },
  ];

  // Determine current breakpoint label
  const getBreakpointLabel = (w) => {
    if (w < tokens.breakpoints.phone)        return { label: 'PHONE',   color: '#DC2626' };
    if (w < tokens.breakpoints.tablet)       return { label: 'TABLET',  color: '#F9A61B' };
    if (w < tokens.breakpoints.sidebarStart) return { label: 'TABLET+', color: '#5B52CC' };
    return                                          { label: 'DESKTOP', color: '#059669' };
  };
  const bp = getBreakpointLabel(previewWidth);

  // Scroll-and-highlight a button in the mockup
  const handleLocate = useCallback((targetId) => {
    const el = document.getElementById(targetId);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.style.outline = `3px solid ${tokens.colors.action}`;
    el.style.outlineOffset = '4px';
    el.style.transition = 'outline 0.1s';
    setTimeout(() => {
      el.style.outline = 'none';
    }, 1800);
  }, []);

  // Locator zone data
  const feedZones = [
    { zone: 'Header', buttons: [
      { id: 'feed-context-toggle', label: 'Locality/Groups', variant: 'inline' },
      { id: 'feed-create', label: 'Create', variant: 'action' },
      { id: 'feed-search', label: 'Search', variant: 'action' },
      { id: 'feed-language', label: 'Language', variant: 'utility' },
      { id: 'feed-profile', label: 'Profile', variant: 'identity' },
    ]},
    { zone: 'Cards', buttons: [
      { id: 'feed-card-thumbsup', label: 'ThumbsUp', variant: 'inline' },
      { id: 'feed-card-thumbsdown', label: 'ThumbsDown', variant: 'inline' },
      { id: 'feed-card-msg', label: 'MessageSquare', variant: 'inline' },
      { id: 'feed-card-star', label: 'Star', variant: 'inline' },
    ]},
    { zone: 'Footer', buttons: [
      { id: 'feed-top', label: 'Go to Top', variant: 'action' },
      { id: 'feed-more', label: 'More', variant: 'action' },
      { id: 'feed-help', label: 'Help', variant: 'identity' },
    ]},
  ];

  const detailZones = [
    { zone: 'Header', buttons: [
      { id: 'detail-back', label: 'Back', variant: 'context' },
      { id: 'detail-profile', label: 'Profile', variant: 'identity' },
    ]},
    { zone: 'Sidebar', buttons: [
      { id: 'detail-share', label: 'Share', variant: 'ghost' },
      { id: 'detail-rate', label: 'Rate', variant: 'inline' },
    ]},
    { zone: 'Comments', buttons: [
      { id: 'detail-thumbsup', label: 'ThumbsUp', variant: 'inline' },
      { id: 'detail-reply', label: 'Reply', variant: 'inline' },
      { id: 'detail-flag', label: 'Flag', variant: 'inline' },
    ]},
    { zone: 'Footer', buttons: [
      { id: 'detail-help', label: 'Help', variant: 'identity' },
    ]},
  ];

  const formZones = [
    { zone: 'Header', buttons: [
      { id: 'form-back', label: 'Back', variant: 'context' },
    ]},
    { zone: 'Footer', buttons: [
      { id: 'form-cancel', label: 'Cancel', variant: 'destructive' },
      { id: 'form-preview', label: 'Preview', variant: 'utility' },
      { id: 'form-draft', label: 'Save Draft', variant: 'utility' },
      { id: 'form-publish', label: 'Publish', variant: 'action' },
      { id: 'form-help', label: 'Help', variant: 'identity' },
    ]},
  ];

  return (
    <div style={{ fontFamily: "'OpenSauceSans', sans-serif", color: '#1A1A1A' }}>

      {/* ================================================================ */}
      {/* SECTION 1: Colour Palette                                         */}
      {/* ================================================================ */}
      <div style={{ marginBottom: '40px' }}>
        <SectionTitle>Colour Palette</SectionTitle>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
          {COLOR_SWATCHES.map(sw => (
            <div key={sw.name} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              background: 'white',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              padding: '8px 12px',
              minWidth: '200px',
            }}>
              <div style={{
                width: '36px',
                height: '36px',
                borderRadius: '6px',
                backgroundColor: sw.hex,
                border: sw.hex === '#FFFFFF' ? '1px solid #E5E7EB' : 'none',
                flexShrink: 0,
              }} />
              <div>
                <div style={{ fontWeight: '600', fontSize: '13px' }}>{sw.name}</div>
                <div style={{ fontSize: '11px', color: '#6B7280', fontFamily: 'monospace' }}>{sw.hex}</div>
                <div style={{ fontSize: '11px', color: '#9CA3AF' }}>{sw.use}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ================================================================ */}
      {/* SECTION 2: Button Variant Gallery                                 */}
      {/* ================================================================ */}
      <div style={{ marginBottom: '40px' }}>
        <SectionTitle>Button Variants</SectionTitle>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {VARIANTS.map(v => (
            <div key={v.id} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              background: 'white',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              padding: '12px 16px',
              flexWrap: 'wrap',
            }}>
              {/* Colour swatch + name */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: '140px' }}>
                <div style={{
                  width: '24px', height: '24px', borderRadius: '4px',
                  backgroundColor: v.color,
                  border: v.color === 'transparent' ? '1px dashed #999' : 'none',
                  flexShrink: 0,
                }} />
                <span style={{ fontWeight: '700', fontSize: '14px' }}>{v.label}</span>
              </div>

              {/* Live button example — context shows both locality + groups colours */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: v.colorAlt ? '280px' : '120px' }}>
                <StandardButton
                  variant={v.id}
                  iconName={v.iconName}
                  label={v.buttonLabel}
                  confirmBefore={false}
                />
                {v.colorAlt && (
                  <>
                    <span style={{ fontSize: '11px', color: '#9CA3AF' }}>·</span>
                    <FakePill color={v.colorAlt} iconChar="←" label="Back" />
                    <span style={{ fontSize: '10px', color: '#9CA3AF', whiteSpace: 'nowrap' }}>locality / groups</span>
                  </>
                )}
              </div>

              {/* Shape badge */}
              <span style={{
                fontSize: '11px', color: '#6B7280',
                background: '#F3F4F6', padding: '2px 8px',
                borderRadius: '10px', whiteSpace: 'nowrap',
              }}>
                {v.shape}
              </span>

              {/* Description */}
              <div style={{ flex: 1, minWidth: '200px' }}>
                <div style={{ fontSize: '13px', color: '#374151' }}>{v.desc}</div>
                <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '2px' }}>Used in: {v.usedIn}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ================================================================ */}
      {/* SECTION 3: Size Reference                                         */}
      {/* ================================================================ */}
      <div style={{ marginBottom: '40px' }}>
        <SectionTitle>Sizes by Shape</SectionTitle>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          {['pill', 'circle', 'ghost', 'inline'].map(shape => {
            const desktop = tokens.button[shape]?.desktop;
            const mobile  = tokens.button[shape]?.mobile;
            return (
              <div key={shape} style={{
                background: 'white',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                padding: '16px',
                minWidth: '200px',
              }}>
                <div style={{ fontWeight: '700', fontSize: '14px', marginBottom: '12px', textTransform: 'capitalize' }}>
                  {shape}
                </div>
                <table style={{ fontSize: '12px', borderCollapse: 'collapse', width: '100%' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', color: '#6B7280', paddingBottom: '4px' }}>Prop</th>
                      <th style={{ textAlign: 'right', color: tokens.colors.contextLocality, paddingBottom: '4px' }}>Desktop</th>
                      <th style={{ textAlign: 'right', color: tokens.colors.contextGroups, paddingBottom: '4px' }}>Mobile</th>
                    </tr>
                  </thead>
                  <tbody>
                    {desktop && Object.entries(desktop).map(([key, val]) => (
                      <tr key={key}>
                        <td style={{ color: '#6B7280', padding: '2px 0' }}>{key}</td>
                        <td style={{ textAlign: 'right', fontFamily: 'monospace', color: tokens.colors.contextLocality }}>{val}px</td>
                        <td style={{ textAlign: 'right', fontFamily: 'monospace', color: tokens.colors.contextGroups }}>{mobile?.[key] ?? '—'}px</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      </div>

      {/* ================================================================ */}
      {/* SECTION 4: Responsive Viewport Preview                            */}
      {/* ================================================================ */}
      <div style={{ marginBottom: '40px' }}>
        <SectionTitle>Page Mockups — Responsive Preview</SectionTitle>

        {/* Control bar */}
        <div style={{
          background: 'white',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          flexWrap: 'wrap',
        }}>
          {/* Slider */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: '200px' }}>
            <span style={{ fontSize: '12px', color: '#6B7280' }}>320px</span>
            <input
              type="range"
              min={320}
              max={1400}
              value={previewWidth}
              onChange={e => setPreviewWidth(Number(e.target.value))}
              style={{ flex: 1, accentColor: tokens.colors.action }}
            />
            <span style={{ fontSize: '12px', color: '#6B7280' }}>1400px</span>
          </div>

          {/* Width readout */}
          <div style={{
            background: '#F3F4F6',
            borderRadius: '6px',
            padding: '4px 12px',
            fontFamily: 'monospace',
            fontSize: '14px',
            fontWeight: '700',
            minWidth: '80px',
            textAlign: 'center',
          }}>
            {previewWidth}px
          </div>

          {/* Breakpoint badge */}
          <div style={{
            background: bp.color,
            color: 'white',
            borderRadius: '12px',
            padding: '4px 12px',
            fontSize: '12px',
            fontWeight: '700',
            letterSpacing: '0.05em',
          }}>
            {bp.label}
          </div>

          {/* Presets */}
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {PRESETS.map(p => (
              <button
                key={p.width}
                onClick={() => setPreviewWidth(p.width)}
                style={{
                  background: previewWidth === p.width ? tokens.colors.contextLocality : '#F3F4F6',
                  color: previewWidth === p.width ? 'white' : '#374151',
                  border: 'none',
                  borderRadius: '20px',
                  padding: '6px 12px',
                  fontSize: '12px',
                  cursor: 'pointer',
                  fontFamily: "'OpenSauceSans', sans-serif",
                  fontWeight: '500',
                  transition: 'background 0.15s, color 0.15s',
                  whiteSpace: 'nowrap',
                }}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Preview container — constrained to selected width */}
        <div style={{ overflowX: 'auto' }}>
          <div
            ref={previewRef}
            style={{
              width: `${previewWidth}px`,
              minWidth: '320px',
              border: '2px solid #E5E7EB',
              borderRadius: '8px',
              background: tokens.colors.headerBackground,
            }}
          >

            {/* ── MOCKUP 1: Feed Page ──────────────────────────────────── */}
            <LocatorBar mockupTitle="Feed Page" zones={feedZones} onLocate={handleLocate} />
            <div style={{ border: '1px solid #E5E7EB', borderRadius: '8px', overflow: 'hidden', marginBottom: '24px' }}>
              {/* Header */}
              <div style={{
                backgroundColor: '#EDF2F8',
                borderBottom: '1px solid #DDE3ED',
                padding: '8px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                minHeight: '52px',
                flexWrap: 'wrap',
              }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '6px', backgroundColor: tokens.colors.contextLocality, flexShrink: 0 }} />
                {/* Context toggle pills */}
                <div id="feed-context-toggle" style={{ display: 'flex', borderRadius: '20px', overflow: 'hidden', border: `2px solid ${tokens.colors.contextLocality}` }}>
                  <div style={{ background: tokens.colors.contextLocality, color: 'white', padding: '4px 14px', fontSize: '12px', fontWeight: '600' }}>Locality</div>
                  <div style={{ background: 'white', color: tokens.colors.contextLocality, padding: '4px 14px', fontSize: '12px', fontWeight: '600' }}>Groups</div>
                </div>
                <div style={{ flex: 1, textAlign: 'center', fontWeight: '700', fontSize: '14px', color: tokens.colors.contextLocality }}>Morningside</div>
                <div id="feed-create"><StandardButton variant="action" iconName="Plus" label="Create" /></div>
                <div id="feed-search"><StandardButton variant="action" iconName="Search" /></div>
                <div id="feed-language"><StandardButton variant="utility" iconName="Globe" /></div>
                <div id="feed-profile"><StandardButton variant="identity" iconName="User" /></div>
              </div>

              {/* Body */}
              <div style={{ display: 'flex', gap: 0, background: '#F9FAFB' }}>
                {previewWidth >= 960 && (
                  <div style={{ width: '200px', flexShrink: 0, background: '#EDF2F8', borderRight: '1px solid #DDE3ED', padding: '12px', minHeight: '200px' }}>
                    <div style={{ fontSize: '11px', color: '#6B7280', fontWeight: '600', marginBottom: '8px' }}>SIDEBAR</div>
                    <div style={{ background: '#024B9920', borderRadius: '6px', height: '80px', marginBottom: '8px' }} />
                    <div style={{ background: '#024B9915', borderRadius: '4px', height: '16px', marginBottom: '6px' }} />
                    <div style={{ background: '#024B9915', borderRadius: '4px', height: '14px', marginBottom: '6px', width: '70%' }} />
                    <div style={{ background: '#024B9915', borderRadius: '4px', height: '14px', width: '85%' }} />
                  </div>
                )}
                <div style={{ flex: 1, padding: '12px', display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${previewWidth < 600 ? '140' : '180'}px, 1fr))`, gap: '10px' }}>
                  {[
                    { title: 'Council Meeting Results', snippet: 'The monthly meeting addressed parking and planning applications for new housing developments...' },
                    { title: 'Summer Farmers Market Returns', snippet: 'The weekly market opens again this Saturday from 9am with local produce and crafts...' },
                    { title: 'Road Closure Notice', snippet: 'High Street will be closed Thursday 8am–6pm for essential utility maintenance work...' },
                    { title: 'Library Opening Hours Changed', snippet: 'From next week the library will close at 7pm on Wednesdays instead of 8pm due to staffing...' },
                    { title: 'Community Garden Volunteers Needed', snippet: 'The park committee needs 10 more volunteers for the autumn clean-up day on 18th October...' },
                    { title: 'New Café Opening Next Week', snippet: 'Bloom Coffee opens Monday at 47 Station Road. First week has 20% discount on all drinks...' },
                  ].map((card, i) => (
                    <div key={i} id={i === 0 ? 'feed-card-thumbsup' : undefined} style={{
                      background: 'white',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      padding: '10px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '6px',
                    }}>
                      <div style={{ background: '#EDF2F8', borderRadius: '4px', height: '60px' }} />
                      <div style={{ fontWeight: '600', fontSize: '12px', color: '#1A1A1A' }}>{card.title}</div>
                      <div style={{ fontSize: '11px', color: '#6B7280', lineHeight: '1.4' }}>{card.snippet.slice(0, 80)}...</div>
                      <div style={{ display: 'flex', gap: '8px', paddingTop: '2px' }}>
                        <span id={i === 0 ? 'feed-card-thumbsup' : undefined}>
                          <StandardButton variant="inline" iconName="ThumbsUp" label={String(8 + i)} />
                        </span>
                        <span id={i === 0 ? 'feed-card-thumbsdown' : undefined}>
                          <StandardButton variant="inline" iconName="ThumbsDown" label={String(i)} />
                        </span>
                        <span id={i === 0 ? 'feed-card-msg' : undefined}>
                          <StandardButton variant="inline" iconName="MessageSquare" label={String(3 + i)} />
                        </span>
                        <span id={i === 0 ? 'feed-card-star' : undefined}>
                          <StandardButton variant="inline" iconName="Star" />
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Footer */}
              <div style={{
                backgroundColor: '#EDF2F8',
                borderTop: '1px solid #DDE3ED',
                padding: '10px 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
                <div style={{ fontSize: '11px', color: '#6B7280' }}>OurNet · The place for conversations</div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <span id="feed-top"><StandardButton variant="action" iconName="ArrowUp" label="Top" showWhen="always" /></span>
                  <span id="feed-more"><StandardButton variant="action" iconName="Plus" label="More" showWhen="always" /></span>
                  <span id="feed-help"><StandardButton variant="identity" iconName="HelpCircle" /></span>
                </div>
              </div>
            </div>

            {/* ── MOCKUP 2: Article Detail Page ────────────────────────── */}
            <LocatorBar mockupTitle="Article Detail Page" zones={detailZones} onLocate={handleLocate} />
            <div style={{ border: '1px solid #E5E7EB', borderRadius: '8px', overflow: 'hidden', marginBottom: '24px' }}>
              {/* Header */}
              <div style={{
                backgroundColor: '#EDF2F8',
                borderBottom: '1px solid #DDE3ED',
                padding: '8px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                minHeight: '52px',
              }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '6px', backgroundColor: tokens.colors.contextLocality, flexShrink: 0 }} />
                <span id="detail-back"><StandardButton variant="context" iconName="ArrowLeft" label="Back" /></span>
                <div style={{ flex: 1, textAlign: 'center', fontWeight: '700', fontSize: '14px', color: tokens.colors.contextLocality }}>Council Meeting Results</div>
                <span id="detail-profile"><StandardButton variant="identity" iconName="User" /></span>
              </div>

              {/* Body */}
              <div style={{ display: 'flex', background: '#F9FAFB' }}>
                {/* Sidebar */}
                {previewWidth >= 800 && (
                  <div style={{ width: '180px', flexShrink: 0, background: '#EDF2F8', borderRight: '1px solid #DDE3ED', padding: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div style={{ background: '#DDE3ED', borderRadius: '6px', height: '100px' }} />
                    <div style={{ fontSize: '11px', color: '#374151', fontWeight: '600' }}>Morningside Community</div>
                    <div style={{ fontSize: '11px', color: '#6B7280' }}>Posted by Jane Smith · 2 days ago</div>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      {[1,2,3,4,5].map(s => (
                        <StandardButton key={s} variant="inline" iconName="Star" />
                      ))}
                    </div>
                    <StandardButton variant="inline" iconName="ThumbsUp" label="24 likes" />
                    <StandardButton variant="inline" iconName="ThumbsDown" label="3" />
                    <span id="detail-share"><StandardButton variant="ghost" iconName="Share2" label="Share" /></span>
                    <span id="detail-rate"><StandardButton variant="inline" iconName="FileText" label="Their articles" /></span>
                    <StandardButton variant="inline" iconName="Flag" label="Report" />
                  </div>
                )}

                {/* Article body */}
                <div style={{ flex: 1, padding: '16px', minHeight: '200px' }}>
                  <div style={{ background: '#DDE3ED', borderRadius: '6px', height: '120px', marginBottom: '12px' }} />
                  {['The monthly council meeting held on Tuesday evening addressed several key issues raised by residents over the past quarter including parking restrictions and planning applications.',
                    'Members voted 8-2 in favour of the new controlled parking zone proposal that will affect High Street and the surrounding residential roads from January next year.',
                    'The three planning applications for new housing on the former industrial site were approved subject to conditions around traffic management and green space provision.'
                  ].map((para, i) => (
                    <p key={i} style={{ fontSize: '13px', color: '#374151', lineHeight: '1.6', marginBottom: '10px' }}>{para}</p>
                  ))}
                </div>

                {/* Comments */}
                {previewWidth >= 900 && (
                  <div style={{ width: '200px', flexShrink: 0, borderLeft: '1px solid #DDE3ED', padding: '12px', background: 'white' }}>
                    <div style={{ fontSize: '11px', fontWeight: '700', color: '#374151', marginBottom: '10px' }}>COMMENTS (12)</div>
                    {['Great news about the parking zone.', 'Has anyone checked the traffic report?', 'Finally some action on the housing.'].map((comment, i) => (
                      <div key={i} style={{ borderBottom: '1px solid #F3F4F6', paddingBottom: '8px', marginBottom: '8px' }}>
                        <div style={{ background: '#F3F4F6', borderRadius: '4px', height: '20px', width: '60%', marginBottom: '4px' }} />
                        <div style={{ fontSize: '11px', color: '#6B7280', lineHeight: '1.4', marginBottom: '6px' }}>{comment}</div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <span id={i === 0 ? 'detail-thumbsup' : undefined}>
                            <StandardButton variant="inline" iconName="ThumbsUp" label={String(i + 2)} />
                          </span>
                          <span id={i === 0 ? 'detail-reply' : undefined}>
                            <StandardButton variant="inline" iconName="Reply" label="Reply" />
                          </span>
                          <span id={i === 0 ? 'detail-flag' : undefined}>
                            <StandardButton variant="inline" iconName="Flag" />
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div style={{
                backgroundColor: '#EDF2F8',
                borderTop: '1px solid #DDE3ED',
                padding: '10px 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
                <div style={{ fontSize: '11px', color: '#6B7280' }}>OurNet · The place for conversations</div>
                <span id="detail-help"><StandardButton variant="identity" iconName="HelpCircle" /></span>
              </div>
            </div>

            {/* ── MOCKUP 3: Form Page ──────────────────────────────────── */}
            <LocatorBar mockupTitle="Form Page (AddEditArticle)" zones={formZones} onLocate={handleLocate} />
            <div style={{ border: '1px solid #E5E7EB', borderRadius: '8px', overflow: 'hidden', marginBottom: '16px' }}>
              {/* Header */}
              <div style={{
                backgroundColor: '#EDF2F8',
                borderBottom: '1px solid #DDE3ED',
                padding: '8px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                minHeight: '52px',
                justifyContent: 'space-between',
              }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '6px', backgroundColor: tokens.colors.contextLocality, flexShrink: 0 }} />
                <div style={{ fontWeight: '700', fontSize: '14px', color: tokens.colors.contextLocality }}>Write Article</div>
                <span id="form-back"><StandardButton variant="context" iconName="ArrowLeft" label="Back" /></span>
              </div>

              {/* Body */}
              <div style={{ display: 'flex', background: '#F9FAFB', minHeight: '200px' }}>
                {/* Image upload area */}
                {previewWidth >= 700 && (
                  <div style={{ width: '180px', flexShrink: 0, background: '#EDF2F8', borderRight: '1px solid #DDE3ED', padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ fontSize: '11px', fontWeight: '700', color: '#374151' }}>COVER IMAGE</div>
                    <div style={{
                      background: 'white', border: '2px dashed #D1D5DB',
                      borderRadius: '8px', height: '100px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <StandardButton variant="inline" iconName="Upload" label="Upload" />
                    </div>
                  </div>
                )}

                {/* Form fields */}
                <div style={{ flex: 1, padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div>
                    <div style={{ fontSize: '11px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>TITLE</div>
                    <div style={{ background: 'white', border: '1px solid #D1D5DB', borderRadius: '6px', padding: '8px 12px', fontSize: '13px', color: '#9CA3AF' }}>
                      Article title...
                    </div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '11px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>BODY</div>
                    <div style={{ background: 'white', border: '1px solid #D1D5DB', borderRadius: '6px', padding: '8px 12px', fontSize: '13px', color: '#9CA3AF', minHeight: '80px' }}>
                      Write your article here...
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '11px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>TAGS</div>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {['council', 'parking', 'housing'].map(tag => (
                        <div key={tag} style={{
                          background: tokens.colors.contextLocality, color: 'white',
                          borderRadius: '12px', padding: '3px 10px', fontSize: '12px',
                          display: 'flex', alignItems: 'center', gap: '5px',
                        }}>
                          {tag} <StandardButton variant="inline" iconName="X" style={{ color: 'white' }} />
                        </div>
                      ))}
                      <StandardButton variant="inline" iconName="Plus" label="Add tag" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div style={{
                backgroundColor: '#EDF2F8',
                borderTop: '1px solid #DDE3ED',
                padding: '10px 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '8px',
                flexWrap: 'wrap',
              }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <span id="form-cancel"><StandardButton variant="destructive" iconName="X" label="Cancel" confirmBefore={false} /></span>
                  <span id="form-preview"><StandardButton variant="utility" iconName="Eye" label="Preview" /></span>
                  <span id="form-draft"><StandardButton variant="utility" iconName="Save" label="Save Draft" /></span>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <span id="form-publish"><StandardButton variant="action" iconName="Send" label="Publish" /></span>
                  <span id="form-help"><StandardButton variant="identity" iconName="HelpCircle" /></span>
                </div>
              </div>
            </div>

          </div>{/* end preview container */}
        </div>{/* end overflow wrapper */}
      </div>

      {/* ================================================================ */}
      {/* SECTION 5: Breakpoint Reference                                   */}
      {/* ================================================================ */}
      <div style={{ marginBottom: '40px' }}>
        <SectionTitle>Breakpoints</SectionTitle>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {[
            { label: 'Phone', range: '< 768px', color: '#DC2626', note: 'MobileDetailHeader, mobile-first layouts' },
            { label: 'Tablet', range: '768–1023px', color: '#F9A61B', note: 'Uses mobile interface sizing' },
            { label: 'Tablet+', range: '1024–1199px', color: '#5B52CC', note: 'Approaching desktop, no sidebar yet' },
            { label: 'Desktop', range: '1200px+', color: '#059669', note: 'Sidebar appears (sidebarStart). Full desktop interface. isMobile=false.' },
            { label: 'Wide', range: '1400px+', color: '#024B99', note: 'Sidebar at full 480px (sidebarFull)' },
          ].map(b => (
            <div key={b.label} style={{
              background: 'white',
              border: `2px solid ${b.color}`,
              borderRadius: '8px',
              padding: '12px 16px',
              minWidth: '180px',
              flex: 1,
            }}>
              <div style={{ fontWeight: '700', fontSize: '14px', color: b.color }}>{b.label}</div>
              <div style={{ fontFamily: 'monospace', fontSize: '13px', color: '#374151', margin: '4px 0' }}>{b.range}</div>
              <div style={{ fontSize: '12px', color: '#6B7280' }}>{b.note}</div>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
};

export default DesignSystemTab;
